import React, { useState, useCallback, useMemo } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Input,
  Label,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  ReferenceLine,
} from 'recharts';
import { dashboardService } from '../../../../services/dashboardService';
import { getOperationTimezone } from '../../../../utils/timezone';
import { apiErrorMessage } from '../../../../utils/apiErrorMessage';
import { ErrorState } from '../../../../components/dashboard/shared/DashboardShared';
import { Table as UiTable, THead, TBody, TR, TH, TD } from '../../../../components/ui/Table';
import { Badge } from '../../../../components/ui/Badge';
import useAuth from 'hooks/useAuth';
import useTheme from 'hooks/useTheme';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

// Mini inline sparkline (pure CSS/SVG). A nivel de módulo: definido dentro del
// componente era un tipo nuevo por render y React desmontaba/remontaba cada
// SVG de la tabla en cada re-render (mismo antipatrón que el ModernChart del
// dashboard).
const MiniSparkline = ({ values, color }) => {
  if (!values || values.length < 2) return <span className="text-muted">—</span>;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const w = 60;
  const h = 20;
  const points = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
    </svg>
  );
};

const COLORS = {
  total: '#3B82F6',
  managed: '#10B981',
  pending: '#EF4444',
  pct: '#8B5CF6',
};

const CLIENT_PALETTE = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
  '#06B6D4', '#A855F7', '#F43F5E', '#22C55E', '#EAB308',
  '#64748B', '#0EA5E9', '#D946EF', '#FB923C', '#4ADE80',
];

const GROUP_OPTIONS = [
  { labelKey: 'Top 5', value: 'top5' },
  { labelKey: 'Top 10', value: 'top10' },
  { labelKey: 'Top 20', value: 'top20' },
  { labelKey: 'Bottom 5', value: 'bottom5' },
  { labelKey: 'Bottom 10', value: 'bottom10' },
  { labelKey: 'Bottom 20', value: 'bottom20' },
  { labelKey: 'Todos', value: 'all' },
];

const RANGE_PRESETS = [
  { labelKey: '4 semanas', weeks: 4 },
  { labelKey: '8 semanas', weeks: 8 },
  { labelKey: '12 semanas', weeks: 12 },
];

const formatWeek = (weekStart) => {
  const d = dayjs(weekStart);
  return `S${d.isoWeek()} ${d.format('DD/MM')}`;
};

const pct = (managed, total) =>
  total > 0 ? ((managed / total) * 100).toFixed(1) : '0.0';

const pctTone = (p) => (p >= 80 ? 'success' : p >= 50 ? 'warning' : 'error');

// Color por excepción: el caso sano (≥80) va apagado; solo la anomalía tiñe.
const pctCellClass = (p) =>
  p >= 80 ? 'text-fg-muted' : p >= 50 ? 'text-status-warning font-semibold' : 'text-status-error font-semibold';

const CustomBarTooltip = ({ active, payload, label }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const total = payload.find(p => p.dataKey === 'total');
  const managed = payload.find(p => p.dataKey === 'managed');
  const pending = payload.find(p => p.dataKey === 'pending');
  const gp = pct(managed?.value ?? 0, total?.value ?? 0);
  return (
    <div className="cv-tooltip">
      <p className="cv-tooltip__label">{label}</p>
      <p className="cv-tooltip__row"><span className="cv-tooltip__dot" style={{ background: COLORS.total }} />{t('Total')}: <strong>{(total?.value ?? 0).toLocaleString()}</strong></p>
      <p className="cv-tooltip__row"><span className="cv-tooltip__dot" style={{ background: COLORS.managed }} />{t('Gestionados')}: <strong>{(managed?.value ?? 0).toLocaleString()}</strong></p>
      <p className="cv-tooltip__row"><span className="cv-tooltip__dot" style={{ background: COLORS.pending }} />{t('Pendientes')}: <strong>{(pending?.value ?? 0).toLocaleString()}</strong></p>
      <p className="cv-tooltip__row"><span className="cv-tooltip__dot" style={{ background: COLORS.pct }} />% {t('Gestión')}: <strong>{gp}%</strong></p>
    </div>
  );
};

const CustomLineTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="cv-tooltip">
      <p className="cv-tooltip__label">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} className="cv-tooltip__row">
          <span className="cv-tooltip__dot" style={{ background: p.color }} />
          {p.name}: <strong>{p.value}%</strong>
        </p>
      ))}
    </div>
  );
};

const SkeletonChart = ({ height = 320 }) => (
  <div className="skeleton-shimmer" style={{ height, borderRadius: 8, marginBottom: '1rem' }} />
);

const WeeklyComparisonTab = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const logisticCompanyId = useMemo(() => user?.logisticCompany?._id || null, [user]);
  const dashboardIanaTz = useMemo(() => getOperationTimezone(user), [user]);

  const tickColor = isDark ? '#D1D5DB' : '#6B7280';
  const gridColor = isDark ? '#374151' : '#E5E7EB';

  const [startDate,    setStartDate]    = useState(() => dayjs().subtract(11, 'week').startOf('isoWeek').format('YYYY-MM-DD'));
  const [endDate,      setEndDate]      = useState(() => dayjs().format('YYYY-MM-DD'));
  const [activePreset, setActivePreset] = useState(null);
  const [group,        setGroup]        = useState('top10');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [detailOpen, setDetailOpen] = useState(true);
  const [expandedClient, setExpandedClient] = useState(null);
  const [detailView, setDetailView] = useState('chart');
  const [highlightedClients, setHighlightedClients] = useState(new Set());

  const handleLineClick = (companyId) => {
    setHighlightedClients(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const applyWeekPreset = (weeks) => {
    setStartDate(dayjs().subtract(weeks - 1, 'week').startOf('isoWeek').format('YYYY-MM-DD'));
    setEndDate(dayjs().format('YYYY-MM-DD'));
    setActivePreset(weeks);
  };

  const [fetchError, setFetchError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!logisticCompanyId) return;
    setLoading(true);
    setHighlightedClients(new Set());
    setFetchError(null);
    try {
      const res = await dashboardService.getClientVolumeWeekly(
        logisticCompanyId,
        startDate,
        endDate,
        dashboardIanaTz,
      );
      setData(res.data);
      setHasLoaded(true);
    } catch (err) {
      // Object, not the string itself: apiErrorMessage() can return null and the
      // state doubles as the "there is an error" flag.
      setFetchError({ message: apiErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, [logisticCompanyId, startDate, endDate, dashboardIanaTz]);

  // Determinar top/bottom clientIds globalmente (por total acumulado)
  const globalRankedIds = useMemo(() => {
    if (!data?.weeks) return [];
    const totals = {};
    for (const week of data.weeks) {
      for (const c of week.clients) {
        if (!totals[c.companyId]) totals[c.companyId] = { companyId: c.companyId, businessName: c.businessName, total: 0 };
        totals[c.companyId].total += c.total;
      }
    }
    const sorted = Object.values(totals).sort((a, b) => b.total - a.total);
    return sorted;
  }, [data]);

  const selectedIds = useMemo(() => {
    if (group === 'all') return globalRankedIds.map(c => c.companyId);
    if (group.startsWith('top')) {
      const n = parseInt(group.replace('top', ''), 10);
      return globalRankedIds.slice(0, n).map(c => c.companyId);
    }
    if (group.startsWith('bottom')) {
      const n = parseInt(group.replace('bottom', ''), 10);
      return [...globalRankedIds].reverse().slice(0, n).map(c => c.companyId);
    }
    return globalRankedIds.map(c => c.companyId);
  }, [globalRankedIds, group]);

  // Datos por semana filtrados por grupo
  const weeklyChartData = useMemo(() => {
    if (!data?.weeks) return [];
    return data.weeks.map(week => {
      const filtered = week.clients.filter(c => selectedIds.includes(c.companyId));
      const total = filtered.reduce((s, c) => s + c.total, 0);
      const managed = filtered.reduce((s, c) => s + c.managed, 0);
      const pending = filtered.reduce((s, c) => s + c.pending, 0);
      return {
        weekLabel: formatWeek(week.weekStart),
        weekStart: week.weekStart,
        total,
        managed,
        pending,
        pct: parseFloat(pct(managed, total)),
      };
    });
  }, [data, selectedIds]);

  // Datos para tabla comparativa
  const tableData = useMemo(() => {
    return weeklyChartData.map((row, i) => {
      const prev = weeklyChartData[i - 1];
      const totalChange = prev ? row.total - prev.total : null;
      const pctChange = prev ? (row.pct - prev.pct).toFixed(1) : null;
      return { ...row, totalChange, pctChange };
    });
  }, [weeklyChartData]);

  const avgPct = weeklyChartData.length > 0
    ? (weeklyChartData.reduce((s, w) => s + w.pct, 0) / weeklyChartData.length).toFixed(1)
    : '0.0';

  // Semanas ordenadas para el detalle por cliente
  const weekLabels = useMemo(() => {
    if (!data?.weeks) return [];
    return data.weeks.map(w => ({ weekStart: w.weekStart, label: formatWeek(w.weekStart) }));
  }, [data]);

  // Detalle por cliente: cada cliente con su array de datos por semana
  const clientDetail = useMemo(() => {
    if (!data?.weeks || selectedIds.length === 0) return [];

    const clientMap = {};
    for (const id of selectedIds) {
      const ranked = globalRankedIds.find(r => r.companyId === id);
      clientMap[id] = {
        companyId: id,
        businessName: ranked?.businessName ?? t('Sin nombre'),
        globalTotal: ranked?.total ?? 0,
        weeks: {},
      };
    }

    for (const week of data.weeks) {
      for (const c of week.clients) {
        if (!clientMap[c.companyId]) continue;
        clientMap[c.companyId].weeks[week.weekStart] = {
          total: c.total,
          managed: c.managed,
          pending: c.pending,
          pct: parseFloat(pct(c.managed, c.total)),
        };
      }
    }

    return Object.values(clientMap).sort((a, b) => b.globalTotal - a.globalTotal);
  }, [data, selectedIds, globalRankedIds, t]);

  const clientEvolutionChartData = useMemo(() => {
    return weekLabels.map(w => {
      const row = { weekLabel: w.label };
      for (const client of clientDetail) {
        row[client.companyId] = client.weeks[w.weekStart]?.total ?? null;
      }
      return row;
    });
  }, [weekLabels, clientDetail]);

  // Mapa companyId -> color (basado en el índice en clientDetail, que es por volumen)
  const clientColorMap = useMemo(() => (
    Object.fromEntries(clientDetail.map((c, i) => [c.companyId, CLIENT_PALETTE[i % CLIENT_PALETTE.length]]))
  ), [clientDetail]);

  // Lista de clientes ordenada alfabéticamente para la leyenda personalizada
  const clientDetailAlpha = useMemo(() => (
    [...clientDetail].sort((a, b) => a.businessName.localeCompare(b.businessName, 'es', { sensitivity: 'base' }))
  ), [clientDetail]);

  const getClientTrend = (client) => {
    const pcts = weekLabels.map(w => client.weeks[w.weekStart]?.pct ?? null).filter(v => v !== null);
    if (pcts.length < 2) return null;
    const last = pcts[pcts.length - 1];
    const first = pcts[0];
    return { diff: (last - first).toFixed(1), direction: last >= first ? 'up' : 'down', values: pcts };
  };

  return (
    <div className="cv-tab">
      {/* Controles */}
      <Card className="chart-card mb-4">
        <CardBody>
          <Row className="align-items-end g-3">
            <Col xs="12" md="auto">
              <Label className="fw-semibold d-block mb-1">{t('Rango rápido')}</Label>
              <div className="cv-presets">
                {RANGE_PRESETS.map(p => (
                  <button
                    key={p.weeks}
                    className={`cv-preset-btn${activePreset === p.weeks ? ' cv-preset-btn--active' : ''}`}
                    onClick={() => applyWeekPreset(p.weeks)}
                  >
                    {t(p.labelKey)}
                  </button>
                ))}
              </div>
            </Col>
            <Col xs="6" md="2">
              <Label for="wc-start" className="fw-semibold d-block mb-1">{t('Desde')}</Label>
              <Input
                id="wc-start"
                type="date"
                value={startDate}
                onChange={e => { setStartDate(e.target.value); setActivePreset(null); }}
                className="dashboard-header__date-input"
              />
            </Col>
            <Col xs="6" md="2">
              <Label for="wc-end" className="fw-semibold d-block mb-1">{t('Hasta')}</Label>
              <Input
                id="wc-end"
                type="date"
                value={endDate}
                onChange={e => { setEndDate(e.target.value); setActivePreset(null); }}
                className="dashboard-header__date-input"
              />
            </Col>
            <Col xs="12" md="2">
              <Label for="wc-group" className="fw-semibold d-block mb-1">{t('Grupo de clientes')}</Label>
              <Input
                id="wc-group"
                type="select"
                value={group}
                onChange={e => setGroup(e.target.value)}
                className="dashboard-header__date-input"
              >
                {GROUP_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
                ))}
              </Input>
            </Col>
            <Col xs="12" md="auto">
              <button
                className="cv-consult-btn"
                onClick={fetchData}
                disabled={loading}
              >
                <i className={`fas ${loading ? 'fa-sync-alt spinning' : 'fa-search'}`} />
                {loading ? t('Cargando…') : t('Consultar')}
              </button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Estado inicial / error */}
      {!hasLoaded && !loading && !fetchError && (
        <div className="cv-empty-state">
          <i className="fas fa-chart-line" />
          <p>{t('Selecciona un rango de semanas y haz clic en')} <strong>{t('Consultar')}</strong> {t('para ver la comparativa semanal.')}</p>
        </div>
      )}
      {fetchError && !loading && (
        <ErrorState message={fetchError.message} onRetry={fetchData} />
      )}

      {(hasLoaded || loading) && (
        <>
          {/* Grouped bar chart: total + gestionados por semana */}
          <Card className="chart-card mb-4">
            <CardHeader className="chart-card__header d-flex align-items-center justify-content-between">
              <h4 className="chart-card__title mb-0">
                {t('Volumen semanal')}
                <Badge tone="brand" className="ms-2 align-middle">
                  {t(GROUP_OPTIONS.find(o => o.value === group)?.labelKey)}
                </Badge>
              </h4>
              {!loading && (
                <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                  {weeklyChartData.length} {t('semana')}{weeklyChartData.length !== 1 ? 's' : ''}
                </span>
              )}
            </CardHeader>
            <CardBody>
              {loading ? (
                <SkeletonChart height={320} />
              ) : weeklyChartData.length === 0 ? (
                <div className="cv-empty-state"><i className="fas fa-inbox" /><p>{t('Sin datos para el rango seleccionado.')}</p></div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={weeklyChartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis dataKey="weekLabel" tick={{ fontSize: 11, fill: tickColor }} />
                    <YAxis tick={{ fontSize: 11, fill: tickColor }} />
                    <Tooltip content={<CustomBarTooltip />} />
                    <Legend formatter={(val) => ({ total: t('Total'), managed: t('Gestionados'), pending: t('Pendientes') })[val] ?? val} wrapperStyle={{ color: tickColor }} />
                    <Bar dataKey="total" name="total" fill={COLORS.total} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="managed" name="managed" fill={COLORS.managed} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="pending" name="pending" fill={COLORS.pending} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardBody>
          </Card>

          {/* Line chart: % gestión por semana */}
          <Card className="chart-card mb-4">
            <CardHeader className="chart-card__header d-flex align-items-center justify-content-between">
              <h4 className="chart-card__title mb-0">{t('Tendencia de % de Gestión')}</h4>
              {!loading && weeklyChartData.length > 0 && (
                <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                  {t('Promedio')}: <strong style={{ color: COLORS.pct }}>{avgPct}%</strong>
                </span>
              )}
            </CardHeader>
            <CardBody>
              {loading ? (
                <SkeletonChart height={260} />
              ) : weeklyChartData.length === 0 ? (
                <div className="cv-empty-state"><i className="fas fa-inbox" /><p>{t('Sin datos.')}</p></div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={weeklyChartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis dataKey="weekLabel" tick={{ fontSize: 11, fill: tickColor }} />
                    <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11, fill: tickColor }} />
                    <Tooltip content={<CustomLineTooltip />} />
                    <Legend formatter={() => `% ${t('Gestión')}`} wrapperStyle={{ color: tickColor }} />
                    <ReferenceLine y={parseFloat(avgPct)} stroke={COLORS.pct} strokeDasharray="6 3" label={{ value: `Prom. ${avgPct}%`, position: 'insideTopRight', fill: COLORS.pct, fontSize: 11 }} />
                    <Line
                      type="monotone"
                      dataKey="pct"
                      name={`% ${t('Gestión')}`}
                      stroke={COLORS.pct}
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: COLORS.pct }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardBody>
          </Card>

          {/* Tabla comparativa agregada */}
          {!loading && tableData.length > 0 && (
            <Card className="chart-card mb-4">
              <CardHeader className="chart-card__header">
                <h4 className="chart-card__title mb-0">{t('Comparativa Semana a Semana')}</h4>
              </CardHeader>
              <CardBody className="p-0 cv-tblwrap">
                <UiTable>
                  <THead>
                    <TR>
                      <TH>{t('Semana')}</TH>
                      <TH className="text-right">{t('Total')}</TH>
                      <TH className="text-right">{t('Gestionados')}</TH>
                      <TH className="text-right">{t('Pendientes')}</TH>
                      <TH className="text-right">% {t('Gestión')}</TH>
                      <TH className="text-right">{t('Var. Total')}</TH>
                      <TH className="text-right">{t('Var. % Gestión')}</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {tableData.map((row) => (
                      <TR key={row.weekStart}>
                        <TD className="align-middle font-semibold whitespace-nowrap">{row.weekLabel}</TD>
                        <TD className="align-middle text-right">{row.total.toLocaleString()}</TD>
                        <TD className="align-middle text-right">{row.managed.toLocaleString()}</TD>
                        <TD className={`align-middle text-right ${row.pending > 0 ? 'text-status-error font-semibold' : 'text-fg-subtle'}`}>
                          {row.pending.toLocaleString()}
                        </TD>
                        <TD className="align-middle text-right">
                          <Badge tone={pctTone(row.pct)} className="min-w-[52px] justify-center">{row.pct}%</Badge>
                        </TD>
                        <TD className="align-middle text-right">
                          {row.totalChange === null ? (
                            <span className="text-fg-subtle">—</span>
                          ) : (
                            <span className={`font-semibold ${row.totalChange >= 0 ? 'text-status-success' : 'text-status-error'}`}>
                              {row.totalChange >= 0 ? '+' : ''}{row.totalChange.toLocaleString()}
                            </span>
                          )}
                        </TD>
                        <TD className="align-middle text-right">
                          {row.pctChange === null ? (
                            <span className="text-fg-subtle">—</span>
                          ) : (
                            <span className={`font-semibold ${parseFloat(row.pctChange) >= 0 ? 'text-status-success' : 'text-status-error'}`}>
                              {parseFloat(row.pctChange) >= 0 ? '+' : ''}{row.pctChange}pp
                            </span>
                          )}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </UiTable>
              </CardBody>
            </Card>
          )}

          {/* Detalle evolución por cliente */}
          {!loading && clientDetail.length > 0 && weekLabels.length > 0 && (
            <Card className="chart-card">
              <CardHeader
                className="chart-card__header d-flex align-items-center justify-content-between"
                style={{ cursor: 'pointer' }}
                onClick={() => setDetailOpen(prev => !prev)}
              >
                <h4 className="chart-card__title mb-0">
                  <i className={`fas fa-chevron-${detailOpen ? 'down' : 'right'} me-2`} style={{ fontSize: '0.75rem', color: '#6B7280' }} />
                  {t('Evolución por Cliente')}
                  <Badge tone="neutral" className="ms-2 align-middle">
                    {clientDetail.length} {t('cliente')}{clientDetail.length !== 1 ? 's' : ''}
                  </Badge>
                </h4>
                <div
                  className="d-flex align-items-center gap-2"
                  onClick={e => e.stopPropagation()}
                >
                  <button
                    className={`cv-view-toggle-btn ${detailView === 'chart' ? 'active' : ''}`}
                    onClick={() => setDetailView('chart')}
                    title={t('Ver gráfico')}
                  >
                    <i className="fas fa-chart-line" />
                  </button>
                  <button
                    className={`cv-view-toggle-btn ${detailView === 'table' ? 'active' : ''}`}
                    onClick={() => setDetailView('table')}
                    title={t('Ver tabla')}
                  >
                    <i className="fas fa-table" />
                  </button>
                </div>
              </CardHeader>
              {detailOpen && (detailView === 'chart' ? (
                  <CardBody>
                    {/* Pills de clientes seleccionados */}
                    {highlightedClients.size > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.75rem', alignItems: 'center' }}>
                        {[...highlightedClients].map(companyId => {
                          const client = clientDetail.find(c => c.companyId === companyId);
                          const color = clientColorMap[companyId];
                          return (
                            <span
                              key={companyId}
                              onClick={() => handleLineClick(companyId)}
                              title={t('Quitar selección')}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.4rem',
                                background: color + '22',
                                border: `1.5px solid ${color}`,
                                borderRadius: 999,
                                padding: '0.25rem 0.65rem 0.25rem 0.5rem',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color,
                                cursor: 'pointer',
                              }}
                            >
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                              {client?.businessName}
                              <i className="fas fa-times" style={{ fontSize: '0.65rem', marginLeft: '0.15rem', opacity: 0.7 }} />
                            </span>
                          );
                        })}
                        {highlightedClients.size > 1 && (
                          <button
                            onClick={() => setHighlightedClients(new Set())}
                            style={{
                              background: 'none',
                              border: '1px solid var(--border-primary)',
                              borderRadius: 999,
                              padding: '0.2rem 0.65rem',
                              fontSize: '0.75rem',
                              color: 'var(--text-secondary)',
                              cursor: 'pointer',
                            }}
                          >
                            {t('Limpiar todo')}
                          </button>
                        )}
                      </div>
                    )}

                    <ResponsiveContainer width="100%" height={420}>
                      <LineChart data={clientEvolutionChartData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis dataKey="weekLabel" tick={{ fontSize: 11, fill: tickColor }} />
                        <YAxis tick={{ fontSize: 11, fill: tickColor }} />
                        <Tooltip
                          formatter={(value, name) => {
                            const client = clientDetail.find(c => c.companyId === name);
                            return [value?.toLocaleString() ?? '—', client?.businessName ?? name];
                          }}
                        />
                        {clientDetail.map(client => {
                          const color = clientColorMap[client.companyId];
                          const isHighlighted = highlightedClients.has(client.companyId);
                          const isDimmed = highlightedClients.size > 0 && !isHighlighted;
                          return (
                            <Line
                              key={client.companyId}
                              type="monotone"
                              dataKey={client.companyId}
                              name={client.businessName}
                              stroke={color}
                              strokeWidth={isHighlighted ? 3 : isDimmed ? 1 : 2}
                              strokeOpacity={isDimmed ? 0.12 : 1}
                              dot={isDimmed ? false : { r: 3 }}
                              activeDot={isDimmed ? false : { r: 5 }}
                              connectNulls
                              onClick={() => handleLineClick(client.companyId)}
                              style={{ cursor: 'pointer' }}
                            />
                          );
                        })}
                      </LineChart>
                    </ResponsiveContainer>

                    {/* Leyenda personalizada: alfabética, scrollable, multiselect */}
                    <div
                      style={{
                        maxHeight: 160,
                        overflowY: 'auto',
                        marginTop: '0.75rem',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.35rem',
                        padding: '0.25rem 0.1rem',
                      }}
                    >
                      {clientDetailAlpha.map(client => {
                        const color = clientColorMap[client.companyId];
                        const isActive = highlightedClients.has(client.companyId);
                        return (
                          <span
                            key={client.companyId}
                            onClick={() => handleLineClick(client.companyId)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.3rem',
                              padding: '0.2rem 0.55rem',
                              borderRadius: 999,
                              fontSize: '0.73rem',
                              fontWeight: isActive ? 700 : 500,
                              cursor: 'pointer',
                              border: `1.5px solid ${color}`,
                              background: isActive ? color + '30' : 'transparent',
                              color: isActive ? color : 'var(--text-secondary)',
                              transition: 'all 0.15s ease',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }} />
                            {client.businessName}
                          </span>
                        );
                      })}
                    </div>
                  </CardBody>
                ) : (
                  <CardBody className="p-0 cv-tblwrap cv-matrix">
                    <UiTable>
                      <THead>
                        <TR>
                          <TH className="cv-matrix__sticky">#</TH>
                          <TH className="cv-matrix__sticky-name">{t('Cliente')}</TH>
                          <TH className="text-center">{t('Tendencia')}</TH>
                          {weekLabels.map(w => (
                            <TH key={w.weekStart} className="text-center whitespace-nowrap">
                              {w.label}
                            </TH>
                          ))}
                          <TH className="text-right">{t('Acum.')}</TH>
                        </TR>
                      </THead>
                      <TBody>
                        {clientDetail.map((client, idx) => {
                          const trend = getClientTrend(client);
                          const isExpanded = expandedClient === client.companyId;
                          const clientColor = CLIENT_PALETTE[idx % CLIENT_PALETTE.length];
                          return (
                            <React.Fragment key={client.companyId}>
                              <TR
                                className="cursor-pointer"
                                onClick={() => setExpandedClient(isExpanded ? null : client.companyId)}
                                aria-expanded={isExpanded}
                              >
                                <TD className="align-middle text-fg-subtle cv-matrix__sticky whitespace-nowrap">
                                  <span
                                    className="cv-client-dot"
                                    style={{ background: clientColor }}
                                    aria-hidden
                                  />
                                  {idx + 1}
                                </TD>
                                <TD className="align-middle font-semibold cv-matrix__sticky-name truncate" title={client.businessName}>
                                  <i className={`fas fa-chevron-right cv-expand-caret${isExpanded ? ' cv-expand-caret--open' : ''} me-2`} />
                                  {client.businessName}
                                </TD>
                                <TD className="align-middle text-center whitespace-nowrap">
                                  {trend ? (
                                    <span className="inline-flex items-center gap-1">
                                      <MiniSparkline values={trend.values} color={trend.direction === 'up' ? COLORS.managed : COLORS.pending} />
                                      <span className={`text-xs font-semibold ${trend.direction === 'up' ? 'text-status-success' : 'text-status-error'}`}>
                                        {trend.direction === 'up' ? '+' : ''}{trend.diff}pp
                                      </span>
                                    </span>
                                  ) : (
                                    <span className="text-fg-subtle text-xs">—</span>
                                  )}
                                </TD>
                                {weekLabels.map(w => {
                                  const cell = client.weeks[w.weekStart];
                                  if (!cell) return <TD key={w.weekStart} className="align-middle text-center text-fg-subtle">—</TD>;
                                  return (
                                    <TD key={w.weekStart} className="align-middle text-center whitespace-nowrap">
                                      <span className="font-semibold">{cell.total.toLocaleString()}</span>
                                      <span className={`ms-1 text-[11px] ${pctCellClass(cell.pct)}`}>{cell.pct}%</span>
                                    </TD>
                                  );
                                })}
                                <TD className="align-middle text-right font-semibold">{client.globalTotal.toLocaleString()}</TD>
                              </TR>
                              {isExpanded && (
                                <TR className="cv-matrix__expanded">
                                  <TD className="cv-matrix__sticky" />
                                  <TD className="cv-matrix__sticky-name text-[11px] uppercase tracking-wider text-fg-muted align-top pt-2">
                                    {t('Desglose')}
                                  </TD>
                                  <TD />
                                  {weekLabels.map(w => {
                                    const cell = client.weeks[w.weekStart];
                                    if (!cell) return <TD key={w.weekStart} />;
                                    return (
                                      <TD key={w.weekStart} className="text-center whitespace-nowrap">
                                        <div className="text-xs">
                                          <span className="text-[11px] uppercase text-fg-subtle">{t('Gest.')}</span>{' '}
                                          {cell.managed.toLocaleString()}
                                        </div>
                                        <div className={`text-xs ${cell.pending > 0 ? 'text-status-error font-semibold' : 'text-fg-subtle'}`}>
                                          <span className="text-[11px] uppercase text-fg-subtle">{t('Pend.')}</span>{' '}
                                          {cell.pending.toLocaleString()}
                                        </div>
                                      </TD>
                                    );
                                  })}
                                  <TD />
                                </TR>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TBody>
                    </UiTable>
                  </CardBody>
                ))}
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// memo: no recibe props; lo aísla de los re-renders del dashboard padre.
export default React.memo(WeeklyComparisonTab);
